/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IFieldProperties
} from '@shared/interfaces/application-objects/field-properties.interface';

/**
 * A class containing static helper methods for building Formly field
 * configurations from JSON Schema data.
 * Provides utilities for field creation, control type inference,
 * label generation, and field property building.
 *
 * @export
 * @class JsonSchemaLayoutHelper
 */
export class JsonSchemaLayoutHelper
{
	/**
	 * Creates a basic Formly field configuration.
	 *
	 * @static
	 * @param {string} key
	 * The field key path.
	 * @param {string} controlType
	 * The Formly control type.
	 * @param {IFieldProperties} fieldProps
	 * The field properties.
	 * @param {boolean} wrapperNeeded
	 * Whether the field needs a wrapper.
	 * @returns {FormlyFieldConfig}
	 * The generated field configuration.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static createField(
		key: string,
		controlType: string,
		fieldProps: IFieldProperties,
		wrapperNeeded: boolean): FormlyFieldConfig
	{
		const field: FormlyFieldConfig =
			{
				key: key,
				type: controlType,
				props: fieldProps
			};

		if (wrapperNeeded)
		{
			field.wrappers =
				[FormlyConstants.customControls.customFieldWrapper];
		}

		return field;
	}

	/**
	 * Creates a repeater field configuration.
	 *
	 * @static
	 * @param {string} key
	 * The field key path.
	 * @param {string} label
	 * The repeater label.
	 * @param {string} singular
	 * The singular form of the label.
	 * @param {(string | null)} titleFormat
	 * The title format string for repeater items.
	 * @param {FormlyFieldConfig[]} fieldGroup
	 * The field group for repeater items.
	 * @param {string} filterValue
	 * The filter function or default value.
	 * @returns {FormlyFieldConfig}
	 * The generated repeater configuration.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static createRepeater(
		key: string,
		label: string,
		singular: string,
		titleFormat: string | null,
		fieldGroup: FormlyFieldConfig[],
		filterValue: string): FormlyFieldConfig
	{
		const repeaterProps: any =
			{
				label: label,
				singular: singular,
				filter: filterValue
			};

		if (!AnyHelper.isNull(titleFormat))
		{
			repeaterProps.titleFormat = titleFormat;
		}

		return {
			key: key,
			type: FormlyConstants.customControls.customRepeater,
			defaultValue: [],
			props: repeaterProps,
			fieldArray: {
				fieldGroup: fieldGroup
			}
		};
	}

	/**
	 * Creates a tab field configuration.
	 *
	 * @static
	 * @param {string} label
	 * The tab label.
	 * @param {FormlyFieldConfig[]} fieldGroup
	 * The field group for tab content.
	 * @returns {FormlyFieldConfig}
	 * The generated tab configuration.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static createTab(
		label: string,
		fieldGroup: FormlyFieldConfig[]): FormlyFieldConfig
	{
		return {
			wrappers: [FormlyConstants.customControls.customTabContent],
			props: {
				label: label
			},
			fieldGroup: fieldGroup
		};
	}

	/**
	 * Creates a section title field configuration.
	 *
	 * @static
	 * @param {string} label
	 * The section title label.
	 * @returns {FormlyFieldConfig}
	 * The generated section title configuration.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static createSectionTitle(label: string): FormlyFieldConfig
	{
		return {
			type: FormlyConstants.customControls.customSectionTitle,
			props: {
				label: label
			}
		};
	}

	/**
	 * Creates a section with a title and field group.
	 *
	 * @static
	 * @param {string} label
	 * The section label.
	 * @param {FormlyFieldConfig[]} fieldGroup
	 * The field group for section content.
	 * @returns {FormlyFieldConfig[]}
	 * Array containing section title and fields.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static createSectionWithTitle(
		label: string,
		fieldGroup: FormlyFieldConfig[]): FormlyFieldConfig[]
	{
		const sectionTitle: FormlyFieldConfig =
			JsonSchemaLayoutHelper.createSectionTitle(label);

		return [sectionTitle, ...fieldGroup];
	}

	/**
	 * Creates an anyOf repeater with a type filter.
	 *
	 * @static
	 * @param {string} key
	 * The field key path.
	 * @param {string} label
	 * The repeater label.
	 * @param {string} typeConst
	 * The type constant value for filtering.
	 * @param {FormlyFieldConfig[]} fieldGroup
	 * The field group for repeater items.
	 * @param {(string | null)} titleFormat
	 * The title format string for repeater items.
	 * @returns {FormlyFieldConfig}
	 * The generated anyOf repeater configuration.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static createAnyOfRepeater(
		key: string,
		label: string,
		typeConst: string,
		fieldGroup: FormlyFieldConfig[],
		titleFormat: string | null): FormlyFieldConfig
	{
		const typeFilterFunction: string =
			'return function(fieldGroup) { '
				+ `return fieldGroup.model.type === "${typeConst}"; }`;

		const repeaterProps: any =
			{
				label: label,
				singular: JsonSchemaLayoutHelper.generateSingular(typeConst),
				filter: typeFilterFunction,
				initialItemValue: {
					type: typeConst
				}
			};

		if (!AnyHelper.isNull(titleFormat))
		{
			repeaterProps.titleFormat = titleFormat;
		}

		return {
			key: key,
			type: FormlyConstants.customControls.customRepeater,
			defaultValue: [],
			props: repeaterProps,
			fieldArray: {
				fieldGroup: fieldGroup
			}
		};
	}

	/**
	 * Creates a summary tab wrapper for non-tab fields.
	 *
	 * @static
	 * @param {FormlyFieldConfig[]} nonTabFields
	 * Fields to wrap in summary tab.
	 * @returns {FormlyFieldConfig}
	 * The generated summary tab configuration.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static createSummaryTab(
		nonTabFields: FormlyFieldConfig[]): FormlyFieldConfig
	{
		return {
			wrappers: [FormlyConstants.customControls.customTabContent],
			props: {
				label: 'Summary'
			},
			fieldGroup: nonTabFields
		};
	}

	/**
	 * Wraps fields in a tab with a section title.
	 *
	 * @static
	 * @param {string} label
	 * The tab and section title label.
	 * @param {FormlyFieldConfig[]} fields
	 * Fields to wrap in tab.
	 * @returns {FormlyFieldConfig}
	 * The generated tab configuration with section title.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static wrapFieldsInTab(
		label: string,
		fields: FormlyFieldConfig[]): FormlyFieldConfig
	{
		const sectionTitle: FormlyFieldConfig =
			JsonSchemaLayoutHelper.createSectionTitle(label);

		const fieldGroupWithTitle: FormlyFieldConfig[] =
			[sectionTitle, ...fields];

		return {
			wrappers: [FormlyConstants.customControls.customTabContent],
			props: {
				label: label
			},
			fieldGroup: fieldGroupWithTitle
		};
	}

	/**
	 * Infers the Formly control type from a property schema.
	 *
	 * @static
	 * @param {*} property
	 * The property schema definition.
	 * @param {string} propertyName
	 * The property name.
	 * @param {Record<string, string>} schemaTypes
	 * Schema type constants.
	 * @param {Record<string, string>} schemaFormats
	 * Schema format constants.
	 * @param {Record<string, string>} schemaProperties
	 * Schema property name constants.
	 * @param {string[]} imageTypes
	 * Property names that should use image input.
	 * @param {string[]} inputNumberTypes
	 * Property types that should use number input.
	 * @returns {string}
	 * The inferred control type.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static inferControlType(
		property: any,
		propertyName: string,
		schemaTypes: Record<string, string>,
		schemaFormats: Record<string, string>,
		schemaProperties: Record<string, string>,
		imageTypes: string[],
		inputNumberTypes: string[]): string
	{
		if (JsonSchemaLayoutHelper.isResourceIdentifierField(
			propertyName,
			schemaProperties))
		{
			return FormlyConstants.customControls.customResourceIdentifier;
		}

		if (JsonSchemaLayoutHelper.isImageField(propertyName, imageTypes))
		{
			return FormlyConstants.customControls.customImageInput;
		}

		if (JsonSchemaLayoutHelper.isMultiSelectField(
			property,
			schemaTypes,
			schemaProperties))
		{
			return FormlyConstants.customControls.customMultiSelect;
		}

		if (JsonSchemaLayoutHelper.isEnumOrConstField(
			property,
			schemaProperties))
		{
			return FormlyConstants.customControls.customSelect;
		}

		if (JsonSchemaLayoutHelper.isDateTimeField(
			property,
			schemaFormats,
			schemaProperties))
		{
			return FormlyConstants.customControls.customCalendar;
		}

		const propertyType: string =
			JsonSchemaLayoutHelper.normalizeType(
				property[schemaProperties.type]);

		if (propertyType === schemaTypes.string)
		{
			return JsonSchemaLayoutHelper.inferStringControlType(property);
		}

		return JsonSchemaLayoutHelper.inferBasicControlType(
			propertyType,
			schemaTypes,
			inputNumberTypes);
	}

	/**
	 * Builds field properties configuration.
	 *
	 * @static
	 * @param {*} property
	 * The property schema definition.
	 * @param {string} propertyName
	 * The property name.
	 * @param {string} controlType
	 * The control type.
	 * @param options
	 * Configuration options for the field.
	 * @param {boolean} options.isRequired
	 * Whether the field is required.
	 * @param {boolean} options.isDisabled
	 * Whether the field is disabled.
	 * @param {(any[] | null)} options.enumValues
	 * Enum values from schema.
	 * @param {*} options.constValue
	 * Const value from schema.
	 * @param {*} options.defaultValue
	 * Default value from schema.
	 * @param {string} options.defaultSelectPlaceholder
	 * Default placeholder for select controls.
	 * @returns {IFieldProperties}
	 * The generated field properties.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static buildFieldProps(
		property: any,
		propertyName: string,
		controlType: string,
		options: {
			isRequired: boolean;
			isDisabled: boolean;
			enumValues: any[] | null;
			constValue: any;
			defaultValue: any;
			defaultSelectPlaceholder: string;
		}): IFieldProperties
	{
		const props: IFieldProperties =
			{
				label:
					JsonSchemaLayoutHelper.generateLabelFromKey(
						propertyName),
				description: property.description,
				required: options.isRequired,
				disabled: options.isDisabled
			};

		if (!AnyHelper.isNull(options.enumValues))
		{
			JsonSchemaLayoutHelper.applyEnumOptions(
				props,
				options.enumValues,
				options.defaultSelectPlaceholder,
				options.isRequired);
		}

		if (!AnyHelper.isNull(options.constValue)
			&& AnyHelper.isNull(options.enumValues))
		{
			JsonSchemaLayoutHelper.applyConstOptions(
				props,
				options.constValue,
				options.defaultSelectPlaceholder);
		}

		if (!AnyHelper.isNull(options.defaultValue))
		{
			JsonSchemaLayoutHelper.applyDefaultValue(
				props,
				options.defaultValue);
		}

		if (controlType === FormlyConstants.customControls.customInputNumber)
		{
			JsonSchemaLayoutHelper.applyNumberConstraints(props, property);
		}

		if (controlType === FormlyConstants.customControls.customMaskedInput)
		{
			JsonSchemaLayoutHelper.applyMaskPattern(props, property);
		}

		if (controlType === FormlyConstants.customControls.customTextArea)
		{
			JsonSchemaLayoutHelper.applyTextAreaConfig(props, property);
		}

		// Allow large layouts with a placeholder before custom selects update.
		props.updateDataOptions = 'return function() { };';

		return props;
	}

	/**
	 * Transforms enum values to dropdown options.
	 *
	 * @static
	 * @param {any[]} enumValues
	 * Array of enum values.
	 * @returns {{ value: any; label: string }[]}
	 * Array of dropdown options.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static transformEnumToOptions(
		enumValues: any[]): { value: any; label: string }[]
	{
		return enumValues
			.filter((value: any) => !AnyHelper.isNull(value))
			.map((value: any) =>
				({
					value: value,
					label: JsonSchemaLayoutHelper.generateLabel(String(value))
				}));
	}

	/**
	 * Generates a display label from a property name.
	 *
	 * @static
	 * @param {string} propertyName
	 * The property name.
	 * @returns {string}
	 * The generated label.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static generateLabel(propertyName: string): string
	{
		return StringHelper.toProperCase(
			StringHelper.beforeCapitalSpaces(propertyName));
	}

	/**
	 * Generates a label from a property key path.
	 * Extracts the last segment after the final period.
	 *
	 * @static
	 * @param {string} propertyKey
	 * The full property key path.
	 * @returns {string}
	 * The generated label.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static generateLabelFromKey(propertyKey: string): string
	{
		const lastSegment: string =
			StringHelper.getLastSplitValue(
				propertyKey,
				AppConstants.characters.period);
		const pascalCased: string =
			StringHelper.toProperCase(lastSegment);

		return StringHelper.beforeCapitalSpaces(pascalCased);
	}

	/**
	 * Generates a singular form from a plural property name.
	 *
	 * @static
	 * @param {string} propertyName
	 * The property name.
	 * @returns {string}
	 * The singular form.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static generateSingular(propertyName: string): string
	{
		const label: string =
			JsonSchemaLayoutHelper.generateLabel(propertyName);

		return StringHelper.toSingular(label);
	}

	/**
	 * Generates a title format string for repeater items.
	 *
	 * @static
	 * @param {*} itemProperties
	 * The repeater item properties.
	 * @param {number} maxProperties
	 * Maximum properties to include in title format.
	 * @param {Record<string, string>} schemaTypes
	 * Schema type constants.
	 * @param {Record<string, string>} schemaProperties
	 * Schema property name constants.
	 * @param {string[]} imageTypes
	 * Property names that should be excluded.
	 * @returns {(string | null)}
	 * The title format string, or null if no properties available.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static generateTitleFormat(
		itemProperties: any,
		maxProperties: number,
		schemaTypes: Record<string, string>,
		schemaProperties: Record<string, string>,
		imageTypes: string[]): string | null
	{
		const propertyKeys: string[] =
			Object.keys(itemProperties ?? {});

		if (propertyKeys.length === 0)
		{
			return null;
		}

		const displayKeys: string[] = propertyKeys
			.filter(
				(propertyKey: string) =>
				{
					const property: any = itemProperties[propertyKey];
					const propertyType: string =
						JsonSchemaLayoutHelper.normalizeType(property?.type);

					if (propertyKey === schemaProperties.resourceIdentifier
						|| propertyKey === schemaProperties.type
						|| imageTypes.includes(propertyKey.toLowerCase()))
					{
						return false;
					}

					return propertyType !== schemaTypes.array
						&& propertyType !== schemaTypes.object;
				})
			.slice(0, maxProperties);

		if (displayKeys.length === 0)
		{
			return null;
		}

		return displayKeys
			.map((propertyKey: string) => `{${propertyKey}}`)
			.join(' - ');
	}

	/**
	 * Determines if a control type should have a wrapper.
	 *
	 * @static
	 * @param {string} controlType
	 * The control type.
	 * @param {Record<string, string>} customControls
	 * Custom control constants.
	 * @returns {boolean}
	 * True if wrapper should be added.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static shouldHaveWrapper(
		controlType: string,
		customControls: Record<string, string>): boolean
	{
		const noWrapperTypes: string[] =
			[
				customControls.customRepeater,
				customControls.customResourceIdentifier,
				customControls.customSectionTitle
			];

		return !noWrapperTypes.includes(controlType);
	}

	/**
	 * Filters properties to remove arrays.
	 * Prevents repeaters within repeaters.
	 *
	 * @static
	 * @param {*} properties
	 * The properties object to filter.
	 * @param {string} arrayType
	 * The schema array type constant.
	 * @param {Record<string, string>} schemaProperties
	 * Schema property name constants.
	 * @returns {*}
	 * The filtered properties object.
	 * @memberof JsonSchemaLayoutHelper
	 */
	public static filterRepeaterProperties(
		properties: any,
		arrayType: string,
		schemaProperties: Record<string, string>): any
	{
		const filteredProperties: any = {};

		for (const propName of Object.keys(properties))
		{
			const prop: any = properties[propName];
			const propType: string =
				JsonSchemaLayoutHelper.normalizeType(
					prop?.[schemaProperties.type]);

			if (propType === arrayType)
			{
				continue;
			}

			filteredProperties[propName] = prop;
		}

		return filteredProperties;
	}

	/**
	 * Normalizes a type value that may be a string or array.
	 *
	 * @private
	 * @static
	 * @param {(string | string[])} type
	 * The type value.
	 * @returns {string}
	 * The normalized type string.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static normalizeType(type: string | string[]): string
	{
		if (Array.isArray(type))
		{
			const validType: string | undefined =
				type.find((typeName: string) =>
					typeName !== 'null' &&
						!AnyHelper.isNullOrWhitespace(typeName));

			return validType ?? type[0];
		}

		return type ?? AppConstants.propertyTypes.string;
	}

	/**
	 * Checks if a property is a resource identifier field.
	 *
	 * @private
	 * @static
	 * @param {string} propertyName
	 * The property name to check.
	 * @param {Record<string, string>} schemaProperties
	 * Schema property name constants.
	 * @returns {boolean}
	 * True if the property is a resource identifier field.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static isResourceIdentifierField(
		propertyName: string,
		schemaProperties: Record<string, string>): boolean
	{
		return propertyName === schemaProperties.resourceIdentifier
			|| propertyName.endsWith(schemaProperties.resourceIdentifier);
	}

	/**
	 * Checks if a property should use an image input control.
	 *
	 * @private
	 * @static
	 * @param {string} propertyName
	 * The property name to check.
	 * @param {string[]} imageTypes
	 * Property names that should use image input.
	 * @returns {boolean}
	 * True if the property should use image input.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static isImageField(
		propertyName: string,
		imageTypes: string[]): boolean
	{
		const lowerPropertyName: string = propertyName.toLowerCase();

		return imageTypes.includes(lowerPropertyName);
	}

	/**
	 * Checks if a property is an array with enum items (multi-select).
	 *
	 * @private
	 * @static
	 * @param {*} property
	 * The property schema definition.
	 * @param {Record<string, string>} schemaTypes
	 * Schema type constants.
	 * @param {Record<string, string>} schemaProperties
	 * Schema property name constants.
	 * @returns {boolean}
	 * True if the property is a multi-select field.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static isMultiSelectField(
		property: any,
		schemaTypes: Record<string, string>,
		schemaProperties: Record<string, string>): boolean
	{
		const propertyType: string =
			JsonSchemaLayoutHelper.normalizeType(
				property[schemaProperties.type]);

		if (propertyType === schemaTypes.array)
		{
			const items: any = property[schemaProperties.items];

			return !AnyHelper.isNull(items?.[schemaProperties.enum]);
		}

		return false;
	}

	/**
	 * Checks if a property has enum or const values (select dropdown).
	 *
	 * @private
	 * @static
	 * @param {*} property
	 * The property schema definition.
	 * @param {Record<string, string>} schemaProperties
	 * Schema property name constants.
	 * @returns {boolean}
	 * True if the property has enum or const values.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static isEnumOrConstField(
		property: any,
		schemaProperties: Record<string, string>): boolean
	{
		return !AnyHelper.isNull(property[schemaProperties.enum])
			|| !AnyHelper.isNull(property[schemaProperties.const]);
	}

	/**
	 * Checks if a property is a date or datetime field.
	 *
	 * @private
	 * @static
	 * @param {*} property
	 * The property schema definition.
	 * @param {Record<string, string>} schemaFormats
	 * Schema format constants.
	 * @param {Record<string, string>} schemaProperties
	 * Schema property name constants.
	 * @returns {boolean}
	 * True if the property is a date/datetime field.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static isDateTimeField(
		property: any,
		schemaFormats: Record<string, string>,
		schemaProperties: Record<string, string>): boolean
	{
		const format: string = property[schemaProperties.format];

		return format === schemaFormats.date
			|| format === schemaFormats.dateTime;
	}

	/**
	 * Infers the control type for string properties.
	 *
	 * @private
	 * @static
	 * @param {*} property
	 * The property schema definition.
	 * @returns {string}
	 * The inferred string control type.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static inferStringControlType(property: any): string
	{
		if (!AnyHelper.isNull(property.pattern))
		{
			return FormlyConstants.customControls.customMaskedInput;
		}

		if (!AnyHelper.isNull(property.rows)
			|| !AnyHelper.isNull(property.multiline))
		{
			return FormlyConstants.customControls.customTextArea;
		}

		return FormlyConstants.customControls.input;
	}

	/**
	 * Infers the control type for non-string basic types.
	 *
	 * @private
	 * @static
	 * @param {string} propertyType
	 * The property type.
	 * @param {Record<string, string>} schemaTypes
	 * Schema type constants.
	 * @param {string[]} inputNumberTypes
	 * Property types that should use number input.
	 * @returns {string}
	 * The inferred control type.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static inferBasicControlType(
		propertyType: string,
		schemaTypes: Record<string, string>,
		inputNumberTypes: string[]): string
	{
		if (propertyType === schemaTypes.boolean)
		{
			return FormlyConstants.customControls.customCheckbox;
		}

		if (propertyType === schemaTypes.object)
		{
			return FormlyConstants.customControls.customSectionTitle;
		}

		if (inputNumberTypes.includes(propertyType))
		{
			return FormlyConstants.customControls.customInputNumber;
		}

		return FormlyConstants.customControls.input;
	}

	/**
	 * Applies enum options to field properties.
	 *
	 * @private
	 * @static
	 * @param {IFieldProperties} props
	 * The field properties to modify.
	 * @param {any[]} enumValues
	 * The enum values to apply.
	 * @param {string} defaultSelectPlaceholder
	 * The placeholder text for select controls.
	 * @param {boolean} isRequired
	 * Whether the field is required.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static applyEnumOptions(
		props: IFieldProperties,
		enumValues: any[],
		defaultSelectPlaceholder: string,
		isRequired: boolean): void
	{
		props.options =
			JsonSchemaLayoutHelper.transformEnumToOptions(enumValues);
		props.placeholder = defaultSelectPlaceholder;

		if (!isRequired)
		{
			props.showClear = true;
		}
	}

	/**
	 * Applies const value options to field properties.
	 *
	 * @private
	 * @static
	 * @param {IFieldProperties} props
	 * The field properties to modify.
	 * @param {*} constValue
	 * The const value to apply.
	 * @param {string} defaultSelectPlaceholder
	 * The placeholder text for select controls.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static applyConstOptions(
		props: IFieldProperties,
		constValue: any,
		defaultSelectPlaceholder: string): void
	{
		props.options =
			JsonSchemaLayoutHelper.transformEnumToOptions([constValue]);
		props.placeholder = defaultSelectPlaceholder;
		props.default = constValue;
	}

	/**
	 * Applies default value to field properties.
	 *
	 * @private
	 * @static
	 * @param {IFieldProperties} props
	 * The field properties to modify.
	 * @param {*} defaultValue
	 * The default value to apply.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static applyDefaultValue(
		props: IFieldProperties,
		defaultValue: any): void
	{
		props.default = defaultValue;
	}

	/**
	 * Applies number input constraints to field properties.
	 *
	 * @private
	 * @static
	 * @param {IFieldProperties} props
	 * The field properties to modify.
	 * @param {*} property
	 * The property schema definition.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static applyNumberConstraints(
		props: IFieldProperties,
		property: any): void
	{
		if (!AnyHelper.isNull(property.minimum))
		{
			props.min = property.minimum;
		}

		if (!AnyHelper.isNull(property.maximum))
		{
			props.max = property.maximum;
		}
	}

	/**
	 * Applies mask pattern to field properties.
	 *
	 * @private
	 * @static
	 * @param {IFieldProperties} props
	 * The field properties to modify.
	 * @param {*} property
	 * The property schema definition.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static applyMaskPattern(
		props: IFieldProperties,
		property: any): void
	{
		if (property.pattern)
		{
			props.mask = property.pattern;
		}
	}

	/**
	 * Applies text area configuration to field properties.
	 *
	 * @private
	 * @static
	 * @param {IFieldProperties} props
	 * The field properties to modify.
	 * @param {*} property
	 * The property schema definition.
	 * @memberof JsonSchemaLayoutHelper
	 */
	private static applyTextAreaConfig(
		props: IFieldProperties,
		property: any): void
	{
		if (property.rows)
		{
			props.rows = property.rows;
		}

		props.autoResize = true;
	}
}